import { useState, useEffect } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { formatCurrency, formatDisplayAmount, toBaseCurrency, fromBaseCurrency, API_URL, resolveMediaUrl } from "../lib/utils";
import { ProductImageField, uploadProductImage } from "../components/products/ProductImageField";
import { buildCategoryTree, flattenCategories, categoryPath, productInCategory } from "../lib/categories";
import { PriceListTab } from "../components/products/PriceListTab";
import { TAX_CODES, EXEMPTION_REASONS } from "../lib/fiscal";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  Tag,
  Star,
  Calculator,
  DollarSign,
  Percent,
  TrendingUp,
  AlertTriangle,
  PackageOpen,
  CornerDownRight,
  FileDown,
  ScanBarcode,
} from "lucide-react";
import { BarcodeField, BarcodeToolbar } from "../components/products/BarcodeField";

const ProductsPage = () => {
  const { t } = useLanguage();
  const { token, isManager } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeTab, setActiveTab] = useState("products");

  // Product Modal
  const [productModal, setProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    name_en: "",
    name_es: "",
    description: "",
    cost_price: "",
    margin_type: "percentage",
    margin_value: "30",
    price: "",
    category_id: "",
    image_url: "",
    is_available: true,
    is_popular: false,
    preparation_time: 10,
    // IVA Angola
    tax_code: "NOR",
    tax_rate: 14,
    tax_exemption_reason: "",
    // Stock
    track_stock: false,
    current_stock: 0,
    min_stock: 0,
    stock_unit: "unidade",
    barcode: "",
  });

  // Stock Alerts
  const [stockAlerts, setStockAlerts] = useState({ out_of_stock: [], low_stock: [], total_alerts: 0 });

  // Tax codes Angola
  const taxCodes = TAX_CODES;
  const exemptionReasons = EXEMPTION_REASONS;

  // Category Modal
  const [categoryModal, setCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    name_en: "",
    name_es: "",
    icon: "",
    color: "#F97316",
    order: 0,
    is_active: true,
    kind: "food",
    parent_id: "",
  });

  // Quick Category Modal (from product form)
  const [quickCategoryModal, setQuickCategoryModal] = useState(false);
  const [quickCategoryName, setQuickCategoryName] = useState("");
  const [quickCategoryParent, setQuickCategoryParent] = useState("");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [prodRes, catRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/categories`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products/stock-alerts`, { headers, withCredentials: true }).catch(() => ({ data: { out_of_stock: [], low_stock: [], total_alerts: 0 } })),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setStockAlerts(alertsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Calculate sale price based on cost and margin
  const calculateSalePrice = (costPrice, marginType, marginValue) => {
    const cost = parseFloat(costPrice) || 0;
    const margin = parseFloat(marginValue) || 0;
    
    if (cost === 0) return 0;
    
    if (marginType === "percentage") {
      return cost * (1 + margin / 100);
    } else {
      return cost + margin;
    }
  };

  // Calculate margin from cost and sale price
  const calculateMargin = (costPrice, salePrice, marginType) => {
    const cost = parseFloat(costPrice) || 0;
    const sale = parseFloat(salePrice) || 0;
    
    if (cost === 0) return 0;
    
    if (marginType === "percentage") {
      return ((sale - cost) / cost) * 100;
    } else {
      return sale - cost;
    }
  };

  // Handle cost price change - auto calculate sale price
  const handleCostPriceChange = (value) => {
    const newForm = { ...productForm, cost_price: value };
    if (value && productForm.margin_value) {
      const salePrice = calculateSalePrice(value, productForm.margin_type, productForm.margin_value);
      newForm.price = salePrice.toFixed(2);
    }
    setProductForm(newForm);
  };

  // Handle margin change - auto calculate sale price
  const handleMarginChange = (type, value) => {
    const newForm = { 
      ...productForm, 
      margin_type: type !== undefined ? type : productForm.margin_type,
      margin_value: value !== undefined ? value : productForm.margin_value 
    };
    if (productForm.cost_price) {
      const salePrice = calculateSalePrice(
        productForm.cost_price, 
        type !== undefined ? type : productForm.margin_type,
        value !== undefined ? value : productForm.margin_value
      );
      newForm.price = salePrice.toFixed(2);
    }
    setProductForm(newForm);
  };

  // Handle sale price change - auto calculate margin
  const handleSalePriceChange = (value) => {
    const newForm = { ...productForm, price: value };
    if (value && productForm.cost_price) {
      const margin = calculateMargin(productForm.cost_price, value, productForm.margin_type);
      newForm.margin_value = margin.toFixed(2);
    }
    setProductForm(newForm);
  };

  // Product handlers
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: toBaseCurrency(productForm.price),
        cost_price: toBaseCurrency(productForm.cost_price),
        margin_value: parseFloat(productForm.margin_value) || 0,
        preparation_time: parseInt(productForm.preparation_time),
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.product_id}`, data, {
          headers,
          withCredentials: true,
        });
        toast.success("Produto atualizado!");
      } else {
        const created = await axios.post(`${API_URL}/products`, data, {
          headers,
          withCredentials: true,
        });
        if (pendingImage && created.data?.product_id) {
          try {
            await uploadProductImage(created.data.product_id, pendingImage, headers);
          } catch (err) {
            toast.error("Produto criado, mas a foto não foi carregada");
          }
        }
        toast.success("Produto criado!");
      }
      setPendingImage(null);
      setProductModal(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error("Erro ao guardar produto");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      name_en: product.name_en || "",
      name_es: product.name_es || "",
      description: product.description || "",
      cost_price: product.cost_price ? fromBaseCurrency(product.cost_price).toString() : "",
      margin_type: product.margin_type || "percentage",
      margin_value: product.margin_value?.toString() || "30",
      price: fromBaseCurrency(product.price).toString(),
      category_id: product.category_id,
      image_url: product.image_url || "",
      is_available: product.is_available,
      is_popular: product.is_popular,
      preparation_time: product.preparation_time,
      tax_code: product.tax_code || "NOR",
      tax_rate: product.tax_rate || 14,
      tax_exemption_reason: product.tax_exemption_reason || "",
      // Stock
      track_stock: product.track_stock || false,
      current_stock: product.current_stock || 0,
      min_stock: product.min_stock || 0,
      stock_unit: product.stock_unit || "unidade",
      barcode: product.barcode || "",
    });
    setProductModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Eliminar este produto?")) return;
    try {
      await axios.delete(`${API_URL}/products/${productId}`, {
        headers,
        withCredentials: true,
      });
      toast.success("Produto eliminado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar produto");
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: "",
      name_en: "",
      name_es: "",
      description: "",
      cost_price: "",
      margin_type: "percentage",
      margin_value: "30",
      price: "",
      category_id: "",
      image_url: "",
      is_available: true,
      is_popular: false,
      preparation_time: 10,
      tax_code: "NOR",
      tax_rate: 14,
      tax_exemption_reason: "",
      // Stock
      track_stock: false,
      current_stock: 0,
      min_stock: 0,
      stock_unit: "unidade",
      barcode: "",
    });
  };

  // Quick category creation
  const handleQuickCategorySubmit = async () => {
    if (!quickCategoryName.trim()) return;
    
    try {
      const response = await axios.post(`${API_URL}/categories`, {
        name: quickCategoryName,
        order: categories.length,
        is_active: true,
        color: "#F97316",
        parent_id: quickCategoryParent || null,
      }, { headers, withCredentials: true });
      
      toast.success(quickCategoryParent ? "Subcategoria criada!" : "Categoria criada!");
      setQuickCategoryModal(false);
      setQuickCategoryName("");
      setQuickCategoryParent("");
      
      // Refresh categories and select the new one
      const catRes = await axios.get(`${API_URL}/categories`, { headers, withCredentials: true });
      setCategories(catRes.data);
      setProductForm({ ...productForm, category_id: response.data.category_id });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar categoria");
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...categoryForm,
        order: parseInt(categoryForm.order),
        parent_id: categoryForm.parent_id || null,
      };

      if (editingCategory) {
        await axios.put(`${API_URL}/categories/${editingCategory.category_id}`, data, {
          headers,
          withCredentials: true,
        });
        toast.success(data.parent_id ? "Subcategoria atualizada!" : "Categoria atualizada!");
      } else {
        await axios.post(`${API_URL}/categories`, data, {
          headers,
          withCredentials: true,
        });
        toast.success(data.parent_id ? "Subcategoria criada!" : "Categoria criada!");
      }
      setCategoryModal(false);
      setEditingCategory(null);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar categoria");
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      name_en: category.name_en || "",
      name_es: category.name_es || "",
      icon: category.icon || "",
      color: category.color || "#F97316",
      order: category.order,
      is_active: category.is_active,
      kind: category.kind || "food",
      parent_id: category.parent_id || "",
    });
    setCategoryModal(true);
  };

  const handleAddSubcategory = (parent) => {
    setEditingCategory(null);
    setCategoryForm({
      name: "",
      name_en: "",
      name_es: "",
      icon: "",
      color: parent.color || "#F97316",
      order: (catTree.childrenOf[parent.category_id] || []).length,
      is_active: true,
      kind: parent.kind || "food",
      parent_id: parent.category_id,
    });
    setCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Eliminar esta categoria?")) return;
    try {
      await axios.delete(`${API_URL}/categories/${categoryId}`, {
        headers,
        withCredentials: true,
      });
      toast.success("Categoria eliminada!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao eliminar categoria");
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      name_en: "",
      name_es: "",
      icon: "",
      color: "#F97316",
      order: 0,
      is_active: true,
      kind: "food",
      parent_id: "",
    });
  };

  const catTree = buildCategoryTree(categories);
  const categoryOptions = flattenCategories(categories);

  const filteredProducts = products.filter((product) => {
    const matchesCategory = productInCategory(product, selectedCategory, catTree.childrenOf);
    const q = searchQuery.toLowerCase();
    const matchesSearch = product.name.toLowerCase().includes(q) || (product.barcode && product.barcode.includes(q.replace(/[\s-]/g, "").toUpperCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => categoryPath(categories, categoryId);

  // Calculate profit info
  const getProductProfit = (product) => {
    if (!product.cost_price || product.cost_price === 0) return null;
    const profit = product.price - product.cost_price;
    const profitPercent = (profit / product.cost_price) * 100;
    return { profit, profitPercent };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">{t("products")}</h1>
          <p className="text-muted-foreground">Gestão de produtos e categorias</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="products" className="gap-2">
              <Package className="w-4 h-4" />
              {t("products")}
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <Tag className="w-4 h-4" />
              {t("categories")}
            </TabsTrigger>
            <TabsTrigger value="pricelist" className="gap-2" data-testid="tab-pricelist">
              <FileDown className="w-4 h-4" />
              Lista de Preços (PDF)
            </TabsTrigger>
          </TabsList>

          {isManager && activeTab !== "pricelist" && (
            <Button
              onClick={() => {
                if (activeTab === "products") {
                  setEditingProduct(null);
                  resetProductForm();
                  setProductModal(true);
                } else {
                  setEditingCategory(null);
                  resetCategoryForm();
                  setCategoryModal(true);
                }
              }}
              className="gap-2"
              data-testid="add-btn"
            >
              <Plus className="w-4 h-4" />
              {t("add")}
            </Button>
          )}
        </div>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-4">
          {/* Stock Alerts Banner */}
          {stockAlerts.total_alerts > 0 && (
            <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div className="flex-1">
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    Alertas de Stock ({stockAlerts.total_alerts})
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {stockAlerts.out_of_stock.length > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {stockAlerts.out_of_stock.length} esgotado(s)
                      </Badge>
                    )}
                    {stockAlerts.low_stock.length > 0 && (
                      <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                        {stockAlerts.low_stock.length} com stock baixo
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {stockAlerts.out_of_stock.map(p => p.name).concat(stockAlerts.low_stock.map(p => p.name)).slice(0, 3).join(", ")}
                  {stockAlerts.total_alerts > 3 && "..."}
                </div>
              </div>
            </div>
          )}

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("search") + "..."}
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("all")}</SelectItem>
                {categoryOptions.map((cat) => (
                  <SelectItem key={cat.category_id} value={cat.category_id} data-testid={`filter-category-${cat.category_id}`}>
                    {cat.depth ? `↳ ${cat.name}` : cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isManager && <BarcodeToolbar products={filteredProducts} settings={{}} headers={headers} onChanged={fetchData} />}

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredProducts.map((product) => {
              const profitInfo = getProductProfit(product);
              return (
                <Card key={product.product_id} className="overflow-hidden" data-testid={`product-card-${product.product_id}`}>
                  {product.image_url && (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img
                        src={resolveMediaUrl(product.image_url)}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold">{product.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {getCategoryName(product.category_id)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {product.is_popular && (
                          <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                        )}
                        <Badge variant={product.is_available ? "default" : "secondary"}>
                          {product.is_available ? t("available") : "Indisponível"}
                        </Badge>
                      </div>
                    </div>

                    {product.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {product.description}
                      </p>
                    )}

                    {/* Pricing Info */}
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Preço de Venda:</span>
                        <span className="text-xl font-bold font-mono text-primary">
                          {formatCurrency(product.price)}
                        </span>
                      </div>
                      {product.cost_price > 0 && (
                        <>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Custo:</span>
                            <span className="font-mono">{formatCurrency(product.cost_price)}</span>
                          </div>
                          {profitInfo && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Lucro:</span>
                              <span className="font-mono text-emerald-500">
                                {formatCurrency(profitInfo.profit)} ({profitInfo.profitPercent.toFixed(1)}%)
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Stock Info */}
                    {product.barcode && (
                      <p className="mb-2 text-[11px] font-mono text-muted-foreground flex items-center gap-1" data-testid={`product-barcode-${product.product_id}`}><ScanBarcode className="w-3 h-3" /> {product.barcode}</p>
                    )}
                    {product.track_stock && (
                      <div className="mb-3 p-2 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Stock:</span>
                          <span className={`font-mono font-medium ${
                            product.current_stock <= 0 
                              ? "text-red-500" 
                              : product.current_stock <= product.min_stock 
                                ? "text-amber-500" 
                                : "text-emerald-500"
                          }`}>
                            {product.current_stock} {product.stock_unit || "un."}
                          </span>
                        </div>
                        {product.current_stock <= 0 && (
                          <Badge variant="destructive" className="mt-1 text-xs">
                            ESGOTADO
                          </Badge>
                        )}
                        {product.current_stock > 0 && product.current_stock <= product.min_stock && (
                          <Badge variant="outline" className="mt-1 text-xs border-amber-500 text-amber-500">
                            Stock Baixo
                          </Badge>
                        )}
                      </div>
                    )}

                    {isManager && (
                      <div className="flex justify-end gap-1 pt-2 border-t">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteProduct(product.product_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {filteredProducts.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mb-4 opacity-50" />
                <p>Nenhum produto encontrado</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="pricelist" className="space-y-4">
          <PriceListTab products={products} categories={categories} headers={headers} canInternal={isManager} />
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {catTree.roots.map((category) => {
              const subs = catTree.childrenOf[category.category_id] || [];
              return (
              <Card key={category.category_id} data-testid={`category-card-${category.category_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: category.color + "20" }}
                      >
                        <Tag className="w-5 h-5" style={{ color: category.color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{category.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          Ordem: {category.order} · {subs.length} subcategoria(s)
                        </p>
                        <Badge
                          className={category.kind === "drink" ? "mt-1 bg-cyan-500/15 text-cyan-600 border-cyan-500/30" : "mt-1 bg-amber-500/15 text-amber-600 border-amber-500/30"}
                          data-testid={`category-kind-${category.category_id}`}
                        >
                          {category.kind === "drink" ? "Bebida · Balcão" : "Comida · Cozinha"}
                        </Badge>
                      </div>
                    </div>
                    {isManager && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditCategory(category)}
                          data-testid={`edit-category-${category.category_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteCategory(category.category_id)}
                          data-testid={`delete-category-${category.category_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t space-y-1" data-testid={`subcategories-${category.category_id}`}>
                    {subs.map((sub) => (
                      <div key={sub.category_id} className="flex items-center gap-2 rounded-md bg-muted/40 px-2 py-1.5 text-sm" data-testid={`subcategory-row-${sub.category_id}`}>
                        <CornerDownRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sub.color || category.color }} />
                        <span className="flex-1 truncate">{sub.name}</span>
                        <span className="text-xs text-muted-foreground">{products.filter((p) => p.category_id === sub.category_id).length} art.</span>
                        {isManager && (
                          <>
                            <button type="button" onClick={() => handleEditCategory(sub)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-category-${sub.category_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button type="button" onClick={() => handleDeleteCategory(sub.category_id)} className="text-destructive/70 hover:text-destructive" data-testid={`delete-category-${sub.category_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                    {subs.length === 0 && <p className="text-xs text-muted-foreground px-1">Sem subcategorias</p>}
                    {isManager && (
                      <Button type="button" variant="outline" size="sm" className="w-full mt-1 gap-1.5" onClick={() => handleAddSubcategory(category)} data-testid={`add-subcategory-${category.category_id}`}>
                        <Plus className="w-3.5 h-3.5" /> Subcategoria
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })}

            {categories.length === 0 && (
              <div className="col-span-full flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Tag className="w-12 h-12 mb-4 opacity-50" />
                <p>Nenhuma categoria criada</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Product Modal */}
      <Dialog open={productModal} onOpenChange={(o) => { setProductModal(o); if (!o) setPendingImage(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingProduct ? "Editar Produto" : "Novo Produto"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do produto. O preço de venda pode ser calculado automaticamente.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleProductSubmit} className="space-y-4">
            {/* Names */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Nome (PT) *</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nome (EN)</Label>
                <Input
                  value={productForm.name_en}
                  onChange={(e) => setProductForm({ ...productForm, name_en: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nome (ES)</Label>
                <Input
                  value={productForm.name_es}
                  onChange={(e) => setProductForm({ ...productForm, name_es: e.target.value })}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{t("description")}</Label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Category with quick add */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <div className="flex gap-2">
                <Select
                  value={productForm.category_id}
                  onValueChange={(value) => setProductForm({ ...productForm, category_id: value })}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecionar categoria..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.category_id} value={cat.category_id} data-testid={`product-category-option-${cat.category_id}`}>
                        {cat.depth ? `↳ ${cat.name}` : cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setQuickCategoryModal(true)}
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nova
                </Button>
              </div>
            </div>

            {/* Pricing Section */}
            <Card className="border-dashed">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Cálculo de Preço
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Cost Price */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    Preço de Compra (Custo)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={productForm.cost_price}
                    onChange={(e) => handleCostPriceChange(e.target.value)}
                    data-testid="product-cost-input"
                  />
                </div>

                {/* Margin */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-muted-foreground" />
                    Margem de Lucro
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={productForm.margin_type}
                      onValueChange={(value) => handleMarginChange(value, undefined)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">
                          <span className="flex items-center gap-2">
                            <Percent className="w-3 h-3" />
                            Percentual
                          </span>
                        </SelectItem>
                        <SelectItem value="fixed">
                          <span className="flex items-center gap-2">
                            <DollarSign className="w-3 h-3" />
                            Valor Fixo
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={productForm.margin_type === "percentage" ? "30%" : "0.00"}
                      value={productForm.margin_value}
                      onChange={(e) => handleMarginChange(undefined, e.target.value)}
                      className="flex-1"
                      data-testid="product-margin-input"
                    />
                  </div>
                </div>

                {/* Sale Price */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-primary" />
                    Preço de Venda Final *
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={productForm.price}
                    onChange={(e) => handleSalePriceChange(e.target.value)}
                    required
                    className="text-lg font-bold"
                    data-testid="product-price-input"
                  />
                </div>

                {/* Profit Preview */}
                {productForm.cost_price && productForm.price && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Lucro por unidade:</span>
                      <span className="font-bold text-emerald-500">
                        {formatDisplayAmount(parseFloat(productForm.price) - parseFloat(productForm.cost_price))}
                        {" "}
                        ({((parseFloat(productForm.price) - parseFloat(productForm.cost_price)) / parseFloat(productForm.cost_price) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Image & Time */}
            <ProductImageField
              productId={editingProduct?.product_id}
              imageUrl={productForm.image_url}
              onChange={(url) => setProductForm((f) => ({ ...f, image_url: url }))}
              pendingFile={pendingImage}
              onPendingFile={setPendingImage}
              headers={headers}
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tempo de Preparação (min)</Label>
                <Input
                  type="number"
                  value={productForm.preparation_time}
                  onChange={(e) => setProductForm({ ...productForm, preparation_time: e.target.value })}
                />
              </div>
            </div>

            {/* IVA Angola Section */}
            <Card className="border-amber-500/30">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Percent className="w-4 h-4 text-amber-500" />
                  IVA (Imposto sobre Valor Acrescentado)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Taxa de IVA</Label>
                    <Select
                      value={productForm.tax_code}
                      onValueChange={(value) => {
                        const tax = taxCodes.find(t => t.code === value);
                        setProductForm({ 
                          ...productForm, 
                          tax_code: value,
                          tax_rate: tax?.percentage || 14,
                          tax_exemption_reason: tax?.percentage === 0 ? productForm.tax_exemption_reason : ""
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {taxCodes.map((tax) => (
                          <SelectItem key={tax.code} value={tax.code}>
                            <span className="font-mono mr-2">{tax.code}</span>
                            {tax.description} ({tax.percentage}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Percentagem</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={productForm.tax_rate}
                        onChange={(e) => setProductForm({ ...productForm, tax_rate: parseFloat(e.target.value) || 0 })}
                        className="w-24"
                      />
                      <span className="text-muted-foreground">%</span>
                    </div>
                  </div>
                </div>

                {/* Motivo de Isenção (obrigatório sempre que IVA = 0 — Anexo I n.º 21) */}
                {(productForm.tax_code === "ISE" || productForm.tax_code === "OUT" || Number(productForm.tax_rate) === 0) && (
                  <div className="space-y-2">
                    <Label>Motivo de Isenção *</Label>
                    <Select
                      value={productForm.tax_exemption_reason}
                      onValueChange={(value) => setProductForm({ ...productForm, tax_exemption_reason: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecionar motivo..." />
                      </SelectTrigger>
                      <SelectContent>
                        {exemptionReasons.map((reason) => (
                          <SelectItem key={reason.code} value={reason.code}>
                            <span className="font-mono mr-2">{reason.code}</span>
                            {reason.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Obrigatório para produtos sem IVA (SAF-T Angola)
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Código de barras */}
            <BarcodeField value={productForm.barcode} onChange={(v) => setProductForm({ ...productForm, barcode: v })} productId={editingProduct?.product_id} headers={headers} onGenerated={fetchData} />

            {/* Stock Management */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <PackageOpen className="w-4 h-4" />
                  Gestão de Stock
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.track_stock}
                    onCheckedChange={(checked) => setProductForm({ ...productForm, track_stock: checked })}
                    data-testid="track-stock-switch"
                  />
                  <Label>Controlar Stock deste produto</Label>
                </div>

                {productForm.track_stock && (
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    <div className="space-y-2">
                      <Label>Stock Atual</Label>
                      <Input
                        type="number"
                        min="0"
                        value={productForm.current_stock}
                        onChange={(e) => setProductForm({ ...productForm, current_stock: parseInt(e.target.value) || 0 })}
                        data-testid="current-stock-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stock Mínimo</Label>
                      <Input
                        type="number"
                        min="0"
                        value={productForm.min_stock}
                        onChange={(e) => setProductForm({ ...productForm, min_stock: parseInt(e.target.value) || 0 })}
                        placeholder="Alerta quando atingir"
                        data-testid="min-stock-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade</Label>
                      <Select
                        value={productForm.stock_unit}
                        onValueChange={(value) => setProductForm({ ...productForm, stock_unit: value })}
                      >
                        <SelectTrigger data-testid="stock-unit-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unidade">Unidade</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="litro">Litro</SelectItem>
                          <SelectItem value="porção">Porção</SelectItem>
                          <SelectItem value="fatia">Fatia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {productForm.track_stock && productForm.current_stock <= productForm.min_stock && productForm.current_stock > 0 && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Stock abaixo do mínimo!</span>
                  </div>
                )}

                {productForm.track_stock && productForm.current_stock <= 0 && (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Produto esgotado! Vendas serão bloqueadas.</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Switches */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_available}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_available: checked })}
                />
                <Label>{t("available")}</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_popular}
                  onCheckedChange={(checked) => setProductForm({ ...productForm, is_popular: checked })}
                />
                <Label>{t("popular")}</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setProductModal(false)}>
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="save-product-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Quick Category Modal */}
      <Dialog open={quickCategoryModal} onOpenChange={setQuickCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Categoria Rápida</DialogTitle>
            <DialogDescription>
              Crie uma categoria rapidamente. Pode editar os detalhes depois.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Categoria</Label>
              <Input
                value={quickCategoryName}
                onChange={(e) => setQuickCategoryName(e.target.value)}
                placeholder="Ex: Bebidas, Sobremesas..."
                autoFocus
                data-testid="quick-category-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoria principal (opcional)</Label>
              <Select value={quickCategoryParent || "none"} onValueChange={(v) => setQuickCategoryParent(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="quick-category-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhuma (categoria principal) —</SelectItem>
                  {catTree.roots.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setQuickCategoryModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleQuickCategorySubmit} disabled={!quickCategoryName.trim()}>
              Criar Categoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
      <Dialog open={categoryModal} onOpenChange={setCategoryModal}>
        <DialogContent data-testid="category-modal">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingCategory ? (categoryForm.parent_id ? "Editar Subcategoria" : "Editar Categoria") : (categoryForm.parent_id ? "Nova Subcategoria" : "Nova Categoria")}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Categoria principal (opcional)</Label>
              <Select
                value={categoryForm.parent_id || "none"}
                onValueChange={(v) => {
                  const parent = catTree.byId[v];
                  setCategoryForm({ ...categoryForm, parent_id: v === "none" ? "" : v, kind: parent ? parent.kind || "food" : categoryForm.kind });
                }}
                disabled={!!editingCategory && (catTree.childrenOf[editingCategory.category_id] || []).length > 0}
              >
                <SelectTrigger data-testid="category-parent-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Nenhuma (é categoria principal) —</SelectItem>
                  {catTree.roots.filter((c) => c.category_id !== editingCategory?.category_id).map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id} data-testid={`category-parent-option-${c.category_id}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {editingCategory && (catTree.childrenOf[editingCategory.category_id] || []).length > 0
                  ? "Esta categoria tem subcategorias, por isso continua a ser principal."
                  : categoryForm.parent_id ? `Subcategoria de "${catTree.byId[categoryForm.parent_id]?.name}" — herda o tipo (Comida/Bebida).` : "Deixe vazio para criar uma categoria principal."}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Nome (PT)</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nome (EN)</Label>
                <Input
                  value={categoryForm.name_en}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name_en: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nome (ES)</Label>
                <Input
                  value={categoryForm.name_es}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name_es: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={categoryForm.kind} onValueChange={(v) => setCategoryForm({ ...categoryForm, kind: v })} disabled={!!categoryForm.parent_id}>
                  <SelectTrigger data-testid="category-kind-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="food">Comida (vai para a Cozinha)</SelectItem>
                    <SelectItem value="drink">Bebida (servida no balcão)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cor</Label>
                <Input
                  type="color"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Ordem</Label>
                <Input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, order: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_active: checked })}
              />
              <Label>Ativa</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCategoryModal(false)}>
                {t("cancel")}
              </Button>
              <Button type="submit" data-testid="save-category-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
